import type { Quest } from 'types/quests'
import { isHydratedAnswerCacheEntry } from 'state/cache'
import { answerEmbedsHavePermalinkTargets } from 'utils/shareUtils'

const STREAM_QUEST_HYDRATION_TTL_MS = 60 * 1000
const MAX_STREAM_QUEST_HYDRATION_BATCH_SIZE = 20

const streamQuestHydrationRequestedAt = new Map<string, number>()

function isHydratedStreamAnswer(answer: unknown): boolean {
    return isHydratedAnswerCacheEntry(answer) && answerEmbedsHavePermalinkTargets(answer)
}

function normalizedAnswerIds(value: unknown): string[] {
    return Array.isArray(value)
        ? value
              .map(entry => (entry == null ? null : String(entry)))
              .filter((entry): entry is string => !!entry)
        : []
}

function hasDeclaredStreamAnswers(quest: Quest): boolean {
    return normalizedAnswerIds(quest.sorted_answer_ids).length > 0
}

function hydratedRenderedSortedAnswers(quest: Quest): boolean {
    const previewAnswerIds = normalizedAnswerIds(quest.__knovStreamPreviewAnswerIds)
    const sortedAnswerIds =
        previewAnswerIds.length > 0
            ? previewAnswerIds
            : normalizedAnswerIds(quest.sorted_answer_ids)
    const sortedAnswers = Array.isArray(quest.sorted_answers) ? quest.sorted_answers : []

    if (sortedAnswers.length === 0) return sortedAnswerIds.length === 0
    if (sortedAnswerIds.length === 0) return sortedAnswers.every(isHydratedStreamAnswer)

    const hydratedAnswersById = new Map(
        sortedAnswers
            .filter(isHydratedStreamAnswer)
            .map(answer => [String((answer as { id?: unknown }).id), answer]),
    )
    return sortedAnswerIds.every(answerId => hydratedAnswersById.has(answerId))
}

export function isHydratedStreamQuestEntity(quest: Quest | null | undefined): quest is Quest {
    if (!quest) return false
    if (quest.is_draft || quest.localPublishErrorStatus) return true
    if (quest.parent_id) {
        return isHydratedStreamAnswer(quest.parent) && hydratedRenderedSortedAnswers(quest)
    }
    if (isHydratedStreamAnswer(quest.parent)) return hydratedRenderedSortedAnswers(quest)

    return Array.isArray(quest.sorted_answers)
        ? quest.sorted_answers.length > 0 && hydratedRenderedSortedAnswers(quest)
        : false
}

export function doesStreamQuestNeedHydration(quest: Quest | null | undefined): boolean {
    if (!quest || quest.is_draft || quest.localPublishErrorStatus) return false
    if (quest.parent_id && !isHydratedStreamAnswer(quest.parent)) return true

    const sortedAnswers = Array.isArray(quest.sorted_answers) ? quest.sorted_answers : []
    if (!hasDeclaredStreamAnswers(quest) && sortedAnswers.length === 0) return false

    return !isHydratedStreamQuestEntity(quest)
}

export function isReadyFeedCardProjection(quest: Quest | null | undefined): boolean {
    if (!quest || !isHydratedStreamQuestEntity(quest)) return false

    const feedCard = quest.feed_card
    if (
        feedCard?.version !== 1 ||
        feedCard.availability !== 'available' ||
        typeof feedCard.projection_fingerprint !== 'string' ||
        feedCard.projection_fingerprint.length === 0
    ) {
        return false
    }

    const projectedAnswerIds = normalizedAnswerIds(feedCard.preview_answer_ids)
    const hydratedPreviewAnswerIds = normalizedAnswerIds(quest.__knovStreamPreviewAnswerIds)
    return (
        projectedAnswerIds.length === hydratedPreviewAnswerIds.length &&
        projectedAnswerIds.every((answerId, index) => answerId === hydratedPreviewAnswerIds[index])
    )
}

export function collectStreamQuestIdsNeedingHydration(
    questIds: Array<string | number | null | undefined>,
    quests: Array<Quest | null | undefined>,
): string[] {
    const hydratedIds = new Set<string>()
    const selectedIds: string[] = []
    const seenIds = new Set<string>()

    questIds.forEach((questId, index) => {
        if (questId == null) return
        if (isHydratedStreamQuestEntity(quests[index])) hydratedIds.add(String(questId))
    })

    questIds.forEach((questId, index) => {
        if (questId == null) return

        const normalizedId = String(questId)
        if (hydratedIds.has(normalizedId)) return
        if (seenIds.has(normalizedId)) return

        const quest = quests[index]
        if (quest == null || doesStreamQuestNeedHydration(quest)) {
            seenIds.add(normalizedId)
            selectedIds.push(normalizedId)
        }
    })

    return selectedIds
}

export function selectHydratedStreamQuestEntities(
    quests: Array<Quest | null | undefined>,
): Quest[] {
    return quests.filter(isHydratedStreamQuestEntity)
}

export function selectQuestIdsForStreamQuestHydration({
    questIds,
    seenAt = streamQuestHydrationRequestedAt,
    now = Date.now(),
    ttlMs = STREAM_QUEST_HYDRATION_TTL_MS,
    maxIds = MAX_STREAM_QUEST_HYDRATION_BATCH_SIZE,
}: {
    questIds: string[]
    seenAt?: Map<string, number>
    now?: number
    ttlMs?: number
    maxIds?: number
}): string[] {
    const selectedIds: string[] = []

    for (const questId of questIds) {
        if (selectedIds.length >= maxIds) break

        const lastRequestedAt = seenAt.get(questId) || 0
        if (lastRequestedAt && now - lastRequestedAt < ttlMs) continue

        seenAt.set(questId, now)
        selectedIds.push(questId)
    }

    return selectedIds
}
